
import { apiClient, APIResponse } from './client';

/** Quantity/feature limits for a package -- see api/prisma/seed.ts PACKAGE_DEFINITIONS. -1 means unlimited/custom. Projects and applications are unrestricted on every plan. */
export interface PackageLimits {
  scanLimit?: number;
  teamMemberLimit?: number;
  ortUploadLimit?: number;
  extraUserPrice?: number;
  scanRollover?: boolean;
  trial?: string;
}

export interface Package {
  id: number;
  title: string;
  code: string;
  displayorder?: number;
  packagetype: string;
  validity: number;
  price: number;
  ispaid: boolean;
  features?: any;
  limits?: PackageLimits;
  description?: string;
  isactive: boolean;
}

export interface PackageAddon {
  id: number;
  title: string;
  code: string;
  displayorder?: number;
  validity: number;
  price: number;
  ispaid: boolean;
  features?: any;
  description?: string;
  isactive: boolean;
}

export interface Invoice {
  id: string;
  orderId?: number;
  date: string;
  amount: string;
  status: string;
  description: string;
  downloadUrl: string;
}


export interface Workgroup {
  id: number;
  title: string;
  workgrouptype: string;
  description?: string;
  address?: string;
  city?: string;
  statename?: string;
  country?: string;
  pincode?: string;
  phone?: string;
  email?: string;
  website?: string;
  contactperson?: string;
  contactemail?: string;
  contactphone?: string;
  isactive?: boolean;
  createdon?: string;
  createdby?: number;
}

export interface Subscription {
  id: number;
  workgroupid?: number;
  profileid?: number;
  status: string;
  package: Package;
  packageaddons?: PackageAddon[];
  startfrom: string;
  validupto: string;
  paymenttype?: string;
  description?: string;
  iscurrent?: boolean;
  isactive?: boolean;
  limits?: any;
}

export interface BillingAddressData {
  title: string;
  address: string;
  city: string;
  statename: string;
  pincode: string;
  country: string;
}

export interface ContactSalesData {
  fullName: string;
  organisationName: string;
  workEmail: string;
  areaOfInterest: string;
  message?: string;
}

/**
 * Payload for the unauthenticated endpoint used by the landing-page demo form
 * and the public pricing page. organisationName is optional because the demo
 * form doesn't ask for it.
 */
export interface PublicContactSalesData {
  fullName: string;
  organisationName?: string;
  workEmail: string;
  areaOfInterest: string;
  message?: string;
  source?: 'demo-request' | 'pricing';
}

export class BillingAPI {

  static async getCurrentPackage(): Promise<APIResponse<Subscription | null>> {
    return apiClient.get<Subscription | null>('/billing/current-package');
  }
  static async getPackages() {
    return apiClient.get<Package[]>('/billing/packages');
  }
  static async getPackageAddons() {
    return apiClient.get<PackageAddon[]>('/billing/package-addons');
  }
  static async getInvoiceHistory(): Promise<APIResponse<Invoice[]>> {
    return apiClient.get<Invoice[]>('/billing/invoices');
  }
  static async updatePackage(packageId: number): Promise<APIResponse<{ requiresPayment?: boolean; clientSecret?: string; amount?: number; currency?: string; paymentMethodId?: string; orderId?: number }>> {
    return apiClient.patch<{ requiresPayment?: boolean; clientSecret?: string; amount?: number; currency?: string; paymentMethodId?: string; orderId?: number }>('/billing/package', { packageId });
  }
  static async cancelSubscription(): Promise<APIResponse<void>> {
    return apiClient.patch<void>('/billing/cancel-subscription');
  }
  static async updateBillingAddress(workgroupId: number, data: BillingAddressData): Promise<APIResponse<void>> {
    return apiClient.post<void>(`/profiles/organisation/address/${workgroupId}`, data);
  }

  static async createSetupIntent(): Promise<APIResponse<{ client_secret: string }>> {
    return apiClient.post<{ client_secret: string }>('/billing/create-setup-intent');
  }

  static async createCheckoutSession(returnUrl: string, cancelUrl: string): Promise<APIResponse<{ url: string; session_id: string }>> {
    return apiClient.post<{ url: string; session_id: string }>('/billing/create-checkout-session', {
      returnUrl,
      cancelUrl
    });
  }

  static async getPaymentMethods(): Promise<APIResponse<any[]>> {
    return apiClient.get<any[]>('/billing/payment-methods');
  }

  static async detachPaymentMethod(paymentMethodId: string): Promise<APIResponse<void>> {
    return apiClient.post<void>(`/billing/detach-payment-method/${paymentMethodId}`);
  }

  static async setDefaultPaymentMethod(paymentMethodId: string): Promise<APIResponse<void>> {
    return apiClient.post<void>('/billing/set-default-payment-method', { paymentMethodId });
  }

  static async createPaymentIntent(packageId: number, addonIds?: number[]): Promise<APIResponse<{ client_secret: string; amount: number; currency: string }>> {
    return apiClient.post<{ client_secret: string; amount: number; currency: string }>('/billing/create-payment-intent', {
      packageId,
      addonIds
    });
  }

  static async confirmPayment(paymentIntentId: string, paymentMethodId: string, packageId: number): Promise<APIResponse<{ status: string }>> {
    return apiClient.post<{ status: string }>(`/billing/confirm-payment/${paymentIntentId}`, { paymentMethodId, packageId });
  }

  static async contactSales(data: ContactSalesData): Promise<APIResponse<{ submitted: boolean }>> {
    return apiClient.post<{ submitted: boolean }>('/billing/contact-sales', data);
  }

  /** Public form submissions -- no auth, so this must not hit /billing/*. */
  static async publicContactSales(data: PublicContactSalesData): Promise<APIResponse<{ submitted: boolean }>> {
    return apiClient.post<{ submitted: boolean }>('/public/contact-sales', data);
  }

  static async generateInvoice(orderId: number): Promise<ArrayBuffer> {
    try {

      const token = localStorage.getItem('token');
      if (token) {
        apiClient.authToken = token;
      }

      const buffer = await apiClient.download(`/billing/generate-invoice/${orderId}`);
      return buffer;
    } catch (error) {
      throw new Error('Failed to generate invoice. Please try again.');
    }
  }
};
